import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Check, Star, Plus, ArrowRight } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { getCategory } from "../../constants/categories";

const FALLBACK_IMAGE = "https://placehold.co/400x300/f5f3f0/6b6b6b?text=Image";

const formatPrice = (value) =>
  `${Number(value || 0).toLocaleString("fr-MA")} MAD`;

export default function ProductCard({ p }) {
  const { addToCart, isInCart } = useCart();

  const defaultVariant = p.variants?.[0] ?? null;
  const price = Number(defaultVariant?.price ?? p.price ?? 0);
  const originalPrice = Number(p.originalPrice ?? 0);
  const hasDiscount = originalPrice > price;
  const discount = hasDiscount ? Math.round((1 - price / originalPrice) * 100) : 0;
  const hasMany = (p.variants?.length ?? 0) > 1;

  const rating = Number(p.rating ?? 0);
  const soldCount = Number(p.soldCount ?? 0);

  const added = isInCart(p.id, defaultVariant?.id ?? null);
  const categoryLabel = getCategory(p.category)?.label || p.category;
  const image = p.mainImage || p.images?.[0] || FALLBACK_IMAGE;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (added) return;

    addToCart({
      id: p.id,
      productId: p.id,
      name: p.name,
      category: p.category,
      image,
      price,
      variantId: defaultVariant?.id ?? null,
      variantLabel: defaultVariant?.label ?? null,
      quantity: 1,
    });
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25 }}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
    >
      {/* Image + bouton panier */}
      <div className="relative aspect-square overflow-hidden bg-stone-100">
        <Link to={`/product/${p.id}`} className="block h-full w-full">
          <img
            src={image}
            alt={p.name}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = FALLBACK_IMAGE;
            }}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <button
          type="button"
          onClick={handleAdd}
          aria-label={added ? "Déjà ajouté au panier" : "Ajouter au panier"}
          title={added ? "Déjà ajouté" : "Ajouter au panier"}
          className={`absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full shadow-lg transition ${added
            ? "bg-green-600 text-white"
            : "bg-white text-stone-900 hover:bg-amber-600 hover:text-white"
            }`}
        >
          {added ? (
            <Check size={19} />
          ) : (
            <span className="relative inline-flex">
              <ShoppingCart size={19} />
              <Plus size={12} className="absolute -right-1.5 -top-1.5 rounded-full bg-white" />
            </span>
          )}
        </button>
      </div>

      {/* Informations */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        {categoryLabel && (
          <span className="w-fit rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            {categoryLabel}
          </span>
        )}

        <h3 className="line-clamp-1 text-sm font-semibold text-stone-900 sm:text-base">
          {p.name}
        </h3>

        {/* Prix */}
        <div className="flex flex-wrap items-baseline gap-2">
          {hasMany && <span className="text-[11px] text-stone-500">À partir de</span>}
          <span className="text-lg font-extrabold text-stone-900">{formatPrice(price)}</span>
          {hasDiscount && (
            <>
              <span className="text-xs text-stone-400 line-through">
                {formatPrice(originalPrice)}
              </span>
              <span className="text-xs font-bold text-red-500">-{discount}%</span>
            </>
          )}
        </div>

        {/* Note et ventes */}
        {(rating > 0 || soldCount > 0) && (
          <div className="flex items-center gap-1 text-xs text-stone-600">
            {rating > 0 && (
              <>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={12}
                      className={
                        i < Math.round(rating)
                          ? "fill-stone-900 text-stone-900"
                          : "fill-stone-200 text-stone-200"
                      }
                    />
                  ))}
                </div>
                <span className="font-medium text-stone-700">{rating.toFixed(1)}</span>
              </>
            )}
            {soldCount > 0 && (
              <>
                <span className="text-stone-400">|</span>
                <span>{soldCount.toLocaleString("fr-MA")} vendus</span>
              </>
            )}
          </div>
        )}

        {p.freeShipping && <p className="text-xs text-stone-600">Livraison gratuite</p>}
      </div>
      <Link to={`/product/${p.id}`}  >
        <div className="group/btn flex w-full items-center justify-center gap-2 bg-[#574723] px-4 py-3 text-center text-sm font-semibold text-white transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:bg-[#b8985a] group-hover:shadow-lg group-hover:shadow-[#c9a961]/30 active:scale-[0.98]">
          Voir le produit
          <ArrowRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </div>
      </Link>
    </motion.div>
  );
}